import { useEffect, useState } from "react";

/**
 * useCountdown
 * ------------
 * Counts down from a fixed start point to zero and reports remaining time.
 *
 * @param {object} params
 * @param {string|null} params.startTime   - ISO 8601 string: when the timer began.
 * @param {number}      params.durationMs  - Total duration in milliseconds.
 *
 * @returns {{ days: number, hours: number, minutes: number, seconds: number, isExpired: boolean }}
 */
const useCountdown = ({ startTime, durationMs }) => {
  const calculateRemaining = () => {
    if (!startTime || !durationMs) return null;

    const start = new Date(startTime).getTime();
    const end = start + durationMs;
    const now = Date.now();
    const remaining = end - now;

    return remaining > 0 ? remaining : 0;
  };

  const [remainingMs, setRemainingMs] = useState(calculateRemaining);

  useEffect(() => {
    if (!startTime || !durationMs) {
      setRemainingMs(null);
      return;
    }

    // Recalculate immediately when inputs change
    setRemainingMs(calculateRemaining());

    const interval = setInterval(() => {
      setRemainingMs(prev => {
        if (prev === null) return null;
        const next = prev - 1000;
        return next > 0 ? next : 0;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, durationMs]);

  if (remainingMs === null) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: false };
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const isExpired = remainingMs === 0;

  return { days, hours, minutes, seconds, isExpired };
};

export default useCountdown;
